/* eslint-disable no-use-before-define */
import React, { ReactNode } from 'react';
import { Button, Table } from 'react-bootstrap';
import translate from '../translate';
import { UsageReportDataPoint } from '../../store/usageDataSlice';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSort,
    faSortDown,
    faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import { ApplianceType } from '../../store/applianceSlice';

interface Texts {
    headerTime: string;
    headerAppliance: string;
    headerLaundryRoom: string;
    headerPrice: string;
    headerBookingType: string;
    empty: string;
    download: string;
}

type FormatPriceFunction = (amountAndCurrency: {
    amount: number;
    currency: string;
}) => string;

interface TextsPlus extends Texts {
    _utils: {
        formatPrice: FormatPriceFunction;
    };
}

interface Props {
    strings: TextsPlus;
    contractPeriod: string;
    usageData: UsageReportDataPoint[];
}

enum Columns {
    time = 'time',
    appliance = 'appliance',
    laundryRoom = 'laundryRoom',
    price = 'price',
}

interface RowData {
    key: string;
    time: ReactNode;
    appliance: ReactNode;
    laundryRoom: ReactNode;
    price: ReactNode;
}

const CSV_DELIMITER = ';';

const PRICE_DECIMAL_DOT = 'price (decimal dot)';
const PRICE_DECIMAL_COMMA = 'price (decimal comma)';

const COLUMNS_IN_ORDER = [
    'contract_closed_at_date',
    'contract_closed_at_time',
    'reservation_type',
    'appliance_name',
    'appliance_long_name',
    'laundry_room_name',
    'currency',
    PRICE_DECIMAL_DOT,
    PRICE_DECIMAL_COMMA,
    'booking_type',
];

const COLUMN_TO_HEADER = {
    contract_closed_at_date: 'Date',
    contract_closed_at_time: 'Start of Cycle',
    reservation_type: 'Type',
    appliance_name: 'Short Name',
    appliance_long_name: 'Appliance Description',
    laundry_room_name: 'Laundry Room Name',
    currency: 'Currency',
    [PRICE_DECIMAL_DOT]: 'Price (Decimal Dot)',
    [PRICE_DECIMAL_COMMA]: 'Price (Decimal Comma)',
    booking_type: 'Booking Type',
};

const SPECIAL_COLUMN_TRANSLATIONS: {
    [key: string]: (data: UsageReportDataPoint) => string;
} = {
    [PRICE_DECIMAL_DOT]: transformForDecimalDot,
    [PRICE_DECIMAL_COMMA]: transformForDecimalComma,
    reservation_type: printReservationType,
};

const DOT_FORMATTER = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const COMMA_FORMATTER = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function transformForDecimalDot(data: UsageReportDataPoint): string {
    return DOT_FORMATTER.format(data.price);
}

function transformForDecimalComma(data: UsageReportDataPoint): string {
    return COMMA_FORMATTER.format(data.price);
}

function printReservationType(data: UsageReportDataPoint): string {
    if (!data.reservation_type) {
        return data.reservation_type;
    }
    return data.reservation_type === ApplianceType.WASHING_MACHINE ? 'W' : 'D';
}

enum SortDirection {
    ASC = 1,
    DESC = -1,
}

interface State {
    sorting: {
        field: Columns;
        direction: SortDirection;
    };
}

type SortFunction = (
    a: UsageReportDataPoint,
    b: UsageReportDataPoint
) => number;

class UsageReportTable extends React.Component<Props, State> {
    state: State = {
        sorting: {
            field: Columns.time,
            direction: SortDirection.DESC,
        },
    };

    transformRow = (rowData: UsageReportDataPoint): RowData => {
        return {
            key: String(rowData.contract_closed_at_timestamp),
            time: new Date(
                rowData.contract_closed_at_timestamp
            ).toLocaleString(),
            appliance: rowData.appliance_name,
            laundryRoom: (
                <span className={'width-limited-15'}>
                    {rowData.laundry_room_name}
                </span>
            ),
            price: this.props.strings._utils.formatPrice({
                amount: rowData.price,
                currency: rowData.currency,
            }),
        };
    };

    getSortedRows = (): RowData[] => {
        return this.props.usageData
            .map((it) => it)
            .sort(
                compareByField(
                    mapColumnToField(this.state.sorting.field),
                    this.state.sorting.direction
                )
            )
            .map(this.transformRow);
    };

    handleDownload = () => {
        const csvContent = this.getCsvContent();
        const filename = `${this.props.contractPeriod}.csv`;

        const link = document.createElement('a');
        link.setAttribute(
            'href',
            'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent)
        );
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
    };

    getCsvContent = () => {
        return (
            COLUMNS_IN_ORDER.map((it) => `"${getHeaderTextForCsv(it)}"`).join(
                CSV_DELIMITER
            ) +
            '\n' +
            this.props.usageData.map(transformForCsv).join('\n')
        );
    };

    handleSortClick = (fieldName: Columns) => {
        const direction =
            this.state.sorting.field === fieldName &&
            this.state.sorting.direction === SortDirection.ASC
                ? SortDirection.DESC
                : SortDirection.ASC;
        this.setState({
            sorting: {
                field: fieldName,
                direction,
            },
        });
    };

    handleSortByTime = () => {
        this.handleSortClick(Columns.time);
    };

    handleSortByAppliance = () => {
        this.handleSortClick(Columns.appliance);
    };

    handleSortByLaundryRoom = () => {
        this.handleSortClick(Columns.laundryRoom);
    };

    handleSortByPrice = () => {
        this.handleSortClick(Columns.price);
    };

    renderTableRow = (rowData: RowData) => {
        return (
            <tr key={rowData.key}>
                <td>{rowData.time}</td>
                <td>{rowData.appliance}</td>
                <td>{rowData.laundryRoom}</td>
                <td>{rowData.price}</td>
            </tr>
        );
    };

    _getHeaderText(column: Columns): string {
        switch (column) {
            case Columns.appliance:
                return this.props.strings.headerAppliance;
            case Columns.laundryRoom:
                return this.props.strings.headerLaundryRoom;
            case Columns.price:
                return this.props.strings.headerPrice;
            case Columns.time:
                return this.props.strings.headerTime;
        }
        return '';
    }

    _getHeaderHandleFunction(column: Columns) {
        switch (column) {
            case Columns.appliance:
                return this.handleSortByAppliance;
            case Columns.laundryRoom:
                return this.handleSortByLaundryRoom;
            case Columns.price:
                return this.handleSortByPrice;
            case Columns.time:
                return this.handleSortByTime;
        }
        return this.handleSortByTime;
    }

    renderHeader(column: Columns) {
        const text = this._getHeaderText(column);
        const handleClick = this._getHeaderHandleFunction(column);
        return (
            <th onClick={handleClick} className={'clickable-header'}>
                {text} {this.renderSortArrow(column)}
            </th>
        );
    }

    renderSortArrow(column: Columns) {
        if (column !== this.state.sorting.field) {
            return <FontAwesomeIcon icon={faSort} />;
        }
        const asc = this.state.sorting.direction === SortDirection.ASC;
        return <FontAwesomeIcon icon={asc ? faSortUp : faSortDown} />;
    }

    render() {
        const rows = this.getSortedRows();

        return (
            <div>
                <Button
                    onClick={this.handleDownload}
                    className={'mb-10 download-link btn btn-link'}
                >
                    <FontAwesomeIcon icon={faDownload} className="color-blue" />{' '}
                    {this.props.strings.download}
                </Button>
                <Table striped>
                    <thead>
                        <tr>
                            {this.renderHeader(Columns.time)}
                            {this.renderHeader(Columns.appliance)}
                            {this.renderHeader(Columns.laundryRoom)}
                            {this.renderHeader(Columns.price)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length > 0 ? (
                            rows.map(this.renderTableRow)
                        ) : (
                            <tr>{this.props.strings.empty}</tr>
                        )}
                    </tbody>
                </Table>
            </div>
        );
    }
}

function mapColumnToField(column: Columns): string {
    switch (column) {
        case Columns.time:
            return 'contract_closed_at_timestamp';
        case Columns.appliance:
            return 'appliance_name';
        case Columns.laundryRoom:
            return 'laundry_room_external_id';
        case Columns.price:
            return 'price';
    }
    return 'contract_closed_at_timestamp';
}

function compareByField(
    fieldName: string,
    direction: SortDirection
): SortFunction {
    const multiplier = direction === SortDirection.ASC ? 1 : -1;
    return abstractSortFunction(fieldName);

    function abstractSortFunction(fieldName: string): SortFunction {
        return (a, b) => {
            if (a[fieldName] < b[fieldName]) {
                return -1 * multiplier;
            } else if (a[fieldName] > b[fieldName]) {
                return 1 * multiplier;
            } else {
                return 0;
            }
        };
    }
}

function getHeaderTextForCsv(column: string): string {
    const header = COLUMN_TO_HEADER[column];
    return header || column;
}

function transformForCsv(row: UsageReportDataPoint): string {
    return COLUMNS_IN_ORDER.map((it) => {
        if (typeof SPECIAL_COLUMN_TRANSLATIONS[it] === 'function') {
            return `"${sanitizeForCsvField(SPECIAL_COLUMN_TRANSLATIONS[it](row))}"`;
        } else if (row[it] !== undefined) {
            return `"${sanitizeForCsvField(row[it])}"`;
        } else {
            return '""';
        }
    }).join(CSV_DELIMITER);
}

function sanitizeForCsvField(value: any) {
    if (typeof value === 'string') {
        return value.replace(/"/g, '""');
    } else {
        return value;
    }
}

export default translate('UsageReportTable')(UsageReportTable);
